import type { ProblemDetails } from "@spoonity/flags-contract";
import { getToken, notifyUnauthorized } from "../auth/token";
import { ApiError } from "./errors";

const BASE = "/admin/v1";
/** Console sign-in (CONTRACT §2.4): the Identity Server login the CMS uses. */
const AUTH_BASE = "/auth/v1";

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Raw text body (e.g. CSV import) instead of JSON. */
  rawBody?: { contentType: string; text: string };
  query?: Record<string, string | number | boolean | undefined | null>;
  /** This route needs no auth header (only `GET /admin/v1/config`). */
  anonymous?: boolean;
}

function buildUrl(base: string, path: string, query?: RequestOptions["query"]): string {
  const url = new URL(base + path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

function fallbackProblem(status: number, statusText: string): ProblemDetails {
  return {
    type: "about:blank",
    title: statusText || "Request failed",
    status,
    code: status === 401 ? "unauthorized" : status === 403 ? "forbidden" : "internal_error",
  };
}

/** `/admin/v1/*`. A `401` sends the app back to sign-in. */
export function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return send<T>(BASE, path, options, true);
}

/**
 * `/auth/v1/*`. A `401` here is an answer (wrong password, selection
 * expired), not a lost session, so it never trips the sign-in redirect —
 * the caller shows it.
 */
export function authRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return send<T>(AUTH_BASE, path, options, false);
}

async function send<T>(
  base: string,
  path: string,
  options: RequestOptions,
  redirectOn401: boolean,
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (!options.anonymous) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let body: BodyInit | undefined;
  if (options.rawBody) {
    headers["Content-Type"] = options.rawBody.contentType;
    body = options.rawBody.text;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const res = await fetch(buildUrl(base, path, options.query), {
    method: options.method ?? "GET",
    headers,
    body,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const data = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const problem: ProblemDetails =
      data && typeof data === "object" && "code" in data
        ? (data as ProblemDetails)
        : fallbackProblem(res.status, res.statusText);
    if (res.status === 401 && redirectOn401) notifyUnauthorized();
    throw new ApiError(problem);
  }

  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
